<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  NCard,
  NForm,
  NFormItem,
  NInput,
  NSelect,
  NButton,
  NAlert,
  NSpace,
  NIcon,
  NModal,
  NSteps,
  NStep,
  NDivider,
  NSpin,
  NInputNumber,
  NDatePicker,
  NCheckbox,
  NSwitch,
  NGrid,
  NGridItem,
  NDynamicInput
} from 'naive-ui';
import {
  User,
  Lock,
  Shield,
  Save,
  Eye,
  EyeOff,
  Key,
  ArrowLeft,
  ArrowRight,
  UserCheck,
  Heart,
  Activity,
  Briefcase,
  Building
} from 'lucide-vue-next';

import { authToken } from '@/services/auth';
import {
  createPatientProfile,
  updatePatientProfile,
  fetchMyPatientProfile
} from '@/services/patient';
import { encryptPatientData, decryptPatientData, hashSecurityAnswer, hashSecurityQuestion, derivePasswordFromSecurity } from '@/services/encryption.js';

const router = useRouter();

// Component state
const isLoading = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const isEditMode = ref(false);
const currentStep = ref(1);
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// Password and security setup (Step 1)
const passwordSetup = ref({
  password: '',
  confirmPassword: '',
  securityQuestion1: '',
  securityAnswer1: '',
  securityQuestion2: '',
  securityAnswer2: '',
  securityQuestion3: '',
  securityAnswer3: ''
});

// Complete patient profile data (Steps 2-5) - ALL FIELDS
const patientData = ref({
  dateOfBirth: null,
  gender: "",
  fullAddress: "",
  phoneNumber: "",
  email: "",
  insuranceProvider: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
  emergencyContactRelationship: "",
  medicalHistory: [],
  allergies: [],
  currentMedications: [],
  bloodGroup: "",
  heightCm: null,
  weightKg: null,
  preferredLanguage: "",
  preferredCommunicationMethod: "",
  secondaryInsuranceProvider: "",
  insuranceId: "",
  insuranceGroupNumber: "",
  immunizations: [],
  familyMedicalHistory: [],
  surgicalHistory: [],
  lifestyleInfo: {
    exercise: "",
    smoking: "",
    alcohol: "",
    diet: "",
    sleepHours: null,
    stressLevel: ""
  },
  occupation: "",
  maritalStatus: "",
  preferredPharmacy: "",
  preferredPharmacyAddress: "",
  lastPhysicalExam: null,
  primaryCareProvider: "",
  accessibilityNeeds: [],
  advanceDirectives: false,
  dietaryRestrictions: [],
  consentSigned: false,
  notes: ""
});

// For edit mode - existing profile decryption
const existingPassword = ref('');
const showExistingPassword = ref(false);
const showPasswordModal = ref(false);
const isDecrypting = ref(false);
const originalEncryptedData = ref(null);

// Security question options
const securityQuestions = [
  { label: "What was the name of your first pet?", value: "What was the name of your first pet?" },
  { label: "What city were you born in?", value: "What city were you born in?" },
  { label: "What was your mother's maiden name?", value: "What was your mother's maiden name?" },
  { label: "What was the name of your first school?", value: "What was the name of your first school?" },
  { label: "What was your childhood nickname?", value: "What was your childhood nickname?" },
  { label: "What is your favorite movie?", value: "What is your favorite movie?" },
  { label: "What street did you grow up on?", value: "What street did you grow up on?" },
  { label: "What was the name of your favorite teacher?", value: "What was the name of your favorite teacher?" },
  { label: "What is your favorite food?", value: "What is your favorite food?" },
  { label: "What was the name of your best friend in school?", value: "What was the name of your best friend in school?" },
  { label: "What is your maternal grandmother's first name?", value: "What is your maternal grandmother's first name?" },
  { label: "What was your favorite subject in school?", value: "What was your favorite subject in school?" },
  { label: "What is the name of your hometown?", value: "What is the name of your hometown?" },
  { label: "What was your father's middle name?", value: "What was your father's middle name?" },
  { label: "What was your favorite childhood game?", value: "What was your favorite childhood game?" }
];

// All the options arrays
const bloodGroupOptions = [
  { label: 'A+', value: 'A+' },
  { label: 'A-', value: 'A-' },
  { label: 'B+', value: 'B+' },
  { label: 'B-', value: 'B-' },
  { label: 'AB+', value: 'AB+' },
  { label: 'AB-', value: 'AB-' },
  { label: 'O+', value: 'O+' },
  { label: 'O-', value: 'O-' },
  { label: 'Unknown', value: 'Unknown' }
];

const genderOptions = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
  { label: 'Non-binary', value: 'non-binary' },
  { label: 'Other', value: 'other' },
  { label: 'Prefer not to say', value: 'prefer-not-to-say' }
];

const languageOptions = [
  { label: 'English', value: 'english' },
  { label: 'Hindi', value: 'hindi' },
  { label: 'Bengali', value: 'bengali' },
  { label: 'Telugu', value: 'telugu' },
  { label: 'Marathi', value: 'marathi' },
  { label: 'Tamil', value: 'tamil' },
  { label: 'Urdu', value: 'urdu' },
  { label: 'Gujarati', value: 'gujarati' },
  { label: 'Kannada', value: 'kannada' },
  { label: 'Odia', value: 'odia' },
  { label: 'Punjabi', value: 'punjabi' },
  { label: 'Malayalam', value: 'malayalam' },
  { label: 'Assamese', value: 'assamese' },
  { label: 'Maithili', value: 'maithili' },
  { label: 'Santali', value: 'santali' },
  { label: 'Konkani', value: 'konkani' },
  { label: 'Sindhi', value: 'sindhi' },
  { label: 'Dogri', value: 'dogri' },
  { label: 'Manipuri', value: 'manipuri' },
  { label: 'Bodo', value: 'bodo' },
  { label: 'Kashmiri', value: 'kashmiri' },
  { label: 'Other', value: 'other' }
];

const communicationMethodOptions = [
  { label: 'Phone', value: 'phone' },
  { label: 'Email', value: 'email' },
  { label: 'Text Message', value: 'text' },
  { label: 'Video Call', value: 'video' },
  { label: 'In-Person Only', value: 'in-person' },
  { label: 'Through Caregiver', value: 'caregiver' }
];

const maritalStatusOptions = [
  { label: 'Single', value: 'single' },
  { label: 'Married', value: 'married' },
  { label: 'Divorced', value: 'divorced' },
  { label: 'Widowed', value: 'widowed' },
  { label: 'Separated', value: 'separated' },
  { label: 'Domestic Partnership', value: 'domestic-partnership' },
  { label: 'Prefer not to say', value: 'prefer-not-to-say' }
];

const accessibilityNeedsOptions = [
  { label: 'Wheelchair Access', value: 'wheelchair' },
  { label: 'Hearing Assistance', value: 'hearing' },
  { label: 'Visual Assistance', value: 'visual' },
  { label: 'ASL Interpreter', value: 'asl' },
  { label: 'Language Interpreter', value: 'interpreter' },
  { label: 'Sensory Sensitivities', value: 'sensory' },
  { label: 'Mobility Assistance', value: 'mobility' },
  { label: 'Cognitive/Learning Support', value: 'cognitive' },
  { label: 'Service Animal Accommodation', value: 'service_animal' },
  { label: 'No specific needs', value: 'none' }
];

const exerciseOptions = [
  { label: 'Never', value: 'never' },
  { label: 'Rarely', value: 'rarely' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Several times a week', value: 'several_weekly' },
  { label: 'Daily', value: 'daily' }
];

const smokingOptions = [
  { label: 'Never', value: 'never' },
  { label: 'Former smoker', value: 'former' },
  { label: 'Occasional', value: 'occasional' },
  { label: 'Regular', value: 'regular' }
];

const alcoholOptions = [
  { label: 'Never', value: 'never' },
  { label: 'Rarely', value: 'rarely' },
  { label: 'Socially', value: 'socially' },
  { label: 'Regularly', value: 'regularly' },
  { label: 'Daily', value: 'daily' }
];

const dietOptions = [
  { label: 'Non-vegetarian', value: 'non_vegetarian' },
  { label: 'Vegetarian', value: 'vegetarian' },
  { label: 'Vegan', value: 'vegan' },
  { label: 'Pure Vegetarian (Jain)', value: 'jain' },
  { label: 'Lacto-Vegetarian', value: 'lacto_vegetarian' },
  { label: 'Eggetarian', value: 'eggetarian' },
  { label: 'Satvic', value: 'satvic' },
  { label: 'Halal', value: 'halal' },
  { label: 'Kosher', value: 'kosher' },
  { label: 'Gluten-free', value: 'gluten_free' },
  { label: 'Diabetic-friendly', value: 'diabetic' },
  { label: 'Keto', value: 'keto' },
  { label: 'Paleo', value: 'paleo' },
  { label: 'Other', value: 'other' }
];

const stressLevelOptions = [
  { label: 'Low', value: 'low' },
  { label: 'Moderate', value: 'moderate' },
  { label: 'High', value: 'high' },
  { label: 'Very High', value: 'very_high' }
];

// Dynamic input creator functions
const createMedicalHistoryItem = () => "";
const createAllergyItem = () => "";
const createMedicationItem = () => "";
const createImmunizationItem = () => ({ name: "", date: null });
const createFamilyHistoryItem = () => ({ condition: "", relation: "" });
const createSurgicalHistoryItem = () => ({ procedure: "", date: null });
const createDietaryRestrictionItem = () => "";

// Computed properties
const isPasswordValid = computed(() => {
  return passwordSetup.value.password.length >= 8 &&
    passwordSetup.value.password === passwordSetup.value.confirmPassword;
});

const areSecurityQuestionsValid = computed(() => {
  const setup = passwordSetup.value;
  return setup.securityQuestion1 && setup.securityAnswer1.trim() &&
    setup.securityQuestion2 && setup.securityAnswer2.trim() &&
    setup.securityQuestion3 && setup.securityAnswer3.trim() &&
    setup.securityQuestion1 !== setup.securityQuestion2 &&
    setup.securityQuestion1 !== setup.securityQuestion3 &&
    setup.securityQuestion2 !== setup.securityQuestion3;
});

const canProceedToStep2 = computed(() => {
  return isPasswordValid.value && areSecurityQuestionsValid.value;
});

// Step 2 validation - Essential required fields
const isStep2Valid = computed(() => {
  return patientData.value.dateOfBirth &&
    patientData.value.gender &&
    patientData.value.phoneNumber &&
    patientData.value.emergencyContactName &&
    patientData.value.emergencyContactPhone &&
    patientData.value.consentSigned;
});

// Steps 3, 4, and 5 are optional - always valid
const isStep3Valid = computed(() => true);
const isStep4Valid = computed(() => true);
const isStep5Valid = computed(() => true);

// Final validation for saving
const isProfileDataValid = computed(() => {
  return isStep2Valid.value && patientData.value.consentSigned;
});

// Format date helper function
const formatDateForApi = (timestamp) => {
  if (!timestamp) return null;

  let date;
  if (typeof timestamp === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(timestamp)) {
      return timestamp;
    }
    date = new Date(timestamp);
  } else if (typeof timestamp === 'number') {
    date = new Date(timestamp);
  } else {
    return null;
  }

  if (isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Parse date from API format
const parseDateFromApi = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date.getTime();
};

// Check for existing patient profile
const checkExistingProfile = async () => {
  try {
    isLoading.value = true;
    const profile = await fetchMyPatientProfile(authToken.value);

    if (profile) {
      isEditMode.value = true;
      originalEncryptedData.value = profile;
      showPasswordModal.value = true;
    }
  } catch (err) {
    console.log('No existing profile found:', err.message);
  } finally {
    isLoading.value = false;
  }
};

// Decrypt existing profile and populate form
const decryptExistingProfile = async () => {
  if (!existingPassword.value.trim()) {
    errorMessage.value = "Please enter your password";
    return;
  }

  try {
    isDecrypting.value = true;
    errorMessage.value = '';

    const decryptedData = await decryptPatientData(
      originalEncryptedData.value.encrypted_patient_data,
      originalEncryptedData.value.encryption_metadata,
      existingPassword.value
    );

    // Populate all the form fields with decrypted data
    patientData.value.dateOfBirth = parseDateFromApi(decryptedData.dateOfBirth);
    patientData.value.gender = decryptedData.gender || "";
    patientData.value.fullAddress = decryptedData.fullAddress || "";
    patientData.value.phoneNumber = decryptedData.phoneNumber || "";
    patientData.value.email = decryptedData.email || "";
    patientData.value.insuranceProvider = decryptedData.insuranceProvider || "";
    patientData.value.emergencyContactName = decryptedData.emergencyContactName || "";
    patientData.value.emergencyContactPhone = decryptedData.emergencyContactPhone || "";
    patientData.value.emergencyContactRelationship = decryptedData.emergencyContactRelationship || "";
    patientData.value.medicalHistory = Array.isArray(decryptedData.medicalHistory) ? decryptedData.medicalHistory : [];
    patientData.value.allergies = Array.isArray(decryptedData.allergies) ? decryptedData.allergies : [];
    patientData.value.currentMedications = Array.isArray(decryptedData.currentMedications) ? decryptedData.currentMedications : [];
    patientData.value.bloodGroup = decryptedData.bloodGroup || "";
    patientData.value.heightCm = decryptedData.heightCm || null;
    patientData.value.weightKg = decryptedData.weightKg || null;
    patientData.value.preferredLanguage = decryptedData.preferredLanguage || "";
    patientData.value.preferredCommunicationMethod = decryptedData.preferredCommunicationMethod || "";
    patientData.value.secondaryInsuranceProvider = decryptedData.secondaryInsuranceProvider || "";
    patientData.value.insuranceId = decryptedData.insuranceId || "";
    patientData.value.insuranceGroupNumber = decryptedData.insuranceGroupNumber || "";

    patientData.value.immunizations = Array.isArray(decryptedData.immunizations) ?
      decryptedData.immunizations.map(item => ({
        name: item?.name || "",
        date: parseDateFromApi(item?.date)
      })) : [];

    patientData.value.familyMedicalHistory = Array.isArray(decryptedData.familyMedicalHistory) ?
      decryptedData.familyMedicalHistory.map(item => ({
        condition: item?.condition || "",
        relation: item?.relation || ""
      })) : [];

    patientData.value.surgicalHistory = Array.isArray(decryptedData.surgicalHistory) ?
      decryptedData.surgicalHistory.map(item => ({
        procedure: item?.procedure || "",
        date: parseDateFromApi(item?.date)
      })) : [];

    if (decryptedData.lifestyleInfo && typeof decryptedData.lifestyleInfo === 'object') {
      patientData.value.lifestyleInfo = {
        exercise: decryptedData.lifestyleInfo.exercise || "",
        smoking: decryptedData.lifestyleInfo.smoking || "",
        alcohol: decryptedData.lifestyleInfo.alcohol || "",
        diet: decryptedData.lifestyleInfo.diet || "",
        sleepHours: decryptedData.lifestyleInfo.sleepHours || null,
        stressLevel: decryptedData.lifestyleInfo.stressLevel || ""
      };
    }

    patientData.value.occupation = decryptedData.occupation || "";
    patientData.value.maritalStatus = decryptedData.maritalStatus || "";
    patientData.value.preferredPharmacy = decryptedData.preferredPharmacy || "";
    patientData.value.preferredPharmacyAddress = decryptedData.preferredPharmacyAddress || "";
    patientData.value.lastPhysicalExam = parseDateFromApi(decryptedData.lastPhysicalExam);
    patientData.value.primaryCareProvider = decryptedData.primaryCareProvider || "";
    patientData.value.accessibilityNeeds = Array.isArray(decryptedData.accessibilityNeeds) ? decryptedData.accessibilityNeeds : [];
    patientData.value.advanceDirectives = Boolean(decryptedData.advanceDirectives);
    patientData.value.dietaryRestrictions = Array.isArray(decryptedData.dietaryRestrictions) ? decryptedData.dietaryRestrictions : [];
    patientData.value.consentSigned = Boolean(decryptedData.consentSigned);
    patientData.value.notes = decryptedData.notes || "";

    passwordSetup.value.password = existingPassword.value;
    passwordSetup.value.confirmPassword = existingPassword.value;
    passwordSetup.value.securityQuestion1 = originalEncryptedData.value.security_question_1 || "";
    passwordSetup.value.securityQuestion2 = originalEncryptedData.value.security_question_2 || "";
    passwordSetup.value.securityQuestion3 = originalEncryptedData.value.security_question_3 || "";

    showPasswordModal.value = false;
    currentStep.value = 2;

  } catch (err) {
    console.error('Decryption failed:', err);
    errorMessage.value = "Failed to decrypt profile. Please check your password and try again.";
  } finally {
    isDecrypting.value = false;
  }
};

// Navigation functions
const goToStep1 = () => {
  if (isEditMode.value) return;
  currentStep.value = 1;
  errorMessage.value = '';
};

const goToStep2 = () => {
  if (!isEditMode.value && !canProceedToStep2.value) {
    errorMessage.value = "Please complete the password and security setup first";
    return;
  }
  errorMessage.value = '';
  currentStep.value = 2;
};

const goToStep3 = () => {
  if (!isStep2Valid.value) {
    errorMessage.value = "Please fill in all required fields in Step 2 (Essential Information)";
    return;
  }
  errorMessage.value = '';
  currentStep.value = 3;
};

const goToStep4 = () => {
  errorMessage.value = '';
  currentStep.value = 4;
};

const goToStep5 = () => {
  errorMessage.value = '';
  currentStep.value = 5;
};

const goBackToStep2 = () => {
  currentStep.value = 2;
  errorMessage.value = '';
};

const goBackToStep3 = () => {
  currentStep.value = 3;
  errorMessage.value = '';
};

const goBackToStep4 = () => {
  currentStep.value = 4;
  errorMessage.value = '';
};

// Save profile with password-only encryption
const saveProfile = async () => {
  if (!isProfileDataValid.value) {
    errorMessage.value = "Please fill in all required fields and sign consent";
    return;
  }

  if (!isPasswordValid.value) {
    errorMessage.value = "Password setup is invalid";
    return;
  }

  try {
    isSaving.value = true;
    errorMessage.value = '';
    successMessage.value = '';

    const formDataCopy = { ...patientData.value };

    if (patientData.value.dateOfBirth) {
      formDataCopy.dateOfBirth = formatDateForApi(patientData.value.dateOfBirth);
    }

    if (patientData.value.lastPhysicalExam) {
      formDataCopy.lastPhysicalExam = formatDateForApi(patientData.value.lastPhysicalExam);
    }

    if (patientData.value.immunizations && patientData.value.immunizations.length > 0) {
      formDataCopy.immunizations = patientData.value.immunizations.map(item => {
        if (item.date) {
          return { ...item, date: formatDateForApi(item.date) };
        }
        return item;
      });
    }

    if (patientData.value.surgicalHistory && patientData.value.surgicalHistory.length > 0) {
      formDataCopy.surgicalHistory = patientData.value.surgicalHistory.map(item => {
        if (item.date) {
          return { ...item, date: formatDateForApi(item.date) };
        }
        return item;
      });
    }

    const { encryptedData, encryptionMetadata } = await encryptPatientData(
      formDataCopy,
      passwordSetup.value.password
    );

    const profilePayload = {
      encrypted_patient_data: encryptedData,
      encryption_metadata: encryptionMetadata,
      consent_signed: patientData.value.consentSigned
    };

    const securityKey = await derivePasswordFromSecurity([
      passwordSetup.value.securityAnswer1,
      passwordSetup.value.securityAnswer2,
      passwordSetup.value.securityAnswer3
    ]);

    const { encryptedData: recoveryEncryptedKey, encryptionMetadata: recoveryKeyMetadata } = await encryptPatientData(
      passwordSetup.value.password,
      securityKey
    );

    if (!isEditMode.value) {
      profilePayload.encrypted_recovery_password = recoveryEncryptedKey;
      profilePayload.recovery_encryption_metadata = recoveryKeyMetadata;
      profilePayload.security_question_1 = await hashSecurityQuestion(passwordSetup.value.securityQuestion1);
      profilePayload.security_answer_1_hash = await hashSecurityAnswer(passwordSetup.value.securityAnswer1);
      profilePayload.security_question_2 = await hashSecurityQuestion(passwordSetup.value.securityQuestion2);
      profilePayload.security_answer_2_hash = await hashSecurityAnswer(passwordSetup.value.securityAnswer2);
      profilePayload.security_question_3 = await hashSecurityQuestion(passwordSetup.value.securityQuestion3);
      profilePayload.security_answer_3_hash = await hashSecurityAnswer(passwordSetup.value.securityAnswer3);
    } else {
      profilePayload.encrypted_recovery_password = recoveryEncryptedKey;
      profilePayload.recovery_encryption_metadata = recoveryKeyMetadata;
    }

    if (isEditMode.value) {
      await updatePatientProfile(authToken.value, profilePayload);
      successMessage.value = "Patient profile updated successfully!";
    } else {
      await createPatientProfile(authToken.value, profilePayload);
      successMessage.value = "Patient profile created successfully!";
    }

    setTimeout(() => {
      router.push('/patient-profile');
    }, 2000);

  } catch (err) {
    console.error('Failed to save profile:', err);
    errorMessage.value = err.message || `Failed to ${isEditMode.value ? 'update' : 'create'} patient profile. Please try again.`;
  } finally {
    isSaving.value = false;
  }
};

// Cancel editing
const cancelEdit = () => {
  router.push('/patient-profile');
};

// Initialize component
onMounted(() => {
  checkExistingProfile();
});
</script>

<template>
  <div class="profile-edit-container">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <NSpin size="large" />
      <p>Loading patient profile...</p>
    </div>

    <!-- Password Modal for Edit Mode -->
    <NModal v-model:show="showPasswordModal" preset="card" title="Enter Your Password" :closable="false"
      :mask-closable="false" style="width: 500px">
      <div class="password-modal-content">
        <div class="modal-icon">
          <NIcon size="48" color="#2080f0">
            <Lock />
          </NIcon>
        </div>

        <p>Enter your patient password to decrypt and edit your profile:</p>

        <NForm>
          <NFormItem>
            <NInput v-model:value="existingPassword" :type="showExistingPassword ? 'text' : 'password'"
              placeholder="Enter your patient password" :disabled="isDecrypting" @keyup.enter="decryptExistingProfile">
              <template #suffix>
                <NButton text @click="showExistingPassword = !showExistingPassword" :disabled="isDecrypting">
                  <NIcon>
                    <Eye v-if="!showExistingPassword" />
                    <EyeOff v-else />
                  </NIcon>
                </NButton>
              </template>
            </NInput>
          </NFormItem>
        </NForm>

        <div v-if="errorMessage" class="error-message">
          <NAlert type="error" :title="errorMessage" closable />
        </div>
      </div>

      <template #footer>
        <NSpace justify="end">
          <NButton @click="() => router.push('/patient-profile')" :disabled="isDecrypting">
            Cancel
          </NButton>
          <NButton type="primary" @click="decryptExistingProfile" :loading="isDecrypting"
            :disabled="!existingPassword.trim()">
            <template #icon>
              <NIcon>
                <Key />
              </NIcon>
            </template>
            Decrypt & Edit
          </NButton>
        </NSpace>
      </template>
    </NModal>

    <!-- Main Content -->
    <div class="edit-content">
      <div class="page-header">
        <h1>
          {{ isEditMode ? 'Edit Patient Profile' : 'Create Patient Profile' }}
        </h1>
        <p class="page-subtitle">
          {{ isEditMode ? 'Update your encrypted medical information' : 'Set up your secure, encrypted medical profile'
          }}
        </p>
      </div>

      <!-- Progress Steps -->
      <NSteps :current="currentStep" class="steps-container">
        <NStep title="Security" />
        <NStep title="Essential" />
        <NStep title="Medical" />
        <NStep title="Lifestyle" />
        <NStep title="Insurance" />
      </NSteps>

      <!-- Step 1: Password and Security Setup -->
      <div v-if="currentStep === 1 && !isEditMode">
        <NCard title="Password & Security Setup" class="step-card">
          <template #header-extra>
            <NIcon>
              <Shield />
            </NIcon>
          </template>

          <NAlert type="info" title="Security Notice" class="security-notice">
            Your password and security answers encrypt all your medical data. Choose a strong password and remember your security questions - they're your only way to recover access.
          </NAlert>

          <NForm label-placement="top">
            <!-- Password Setup -->
            <div class="form-section">
              <h3>Create Your Patient Password</h3>

              <NFormItem label="Password (minimum 8 characters)" required>
                <NInput v-model:value="passwordSetup.password" :type="showPassword ? 'text' : 'password'"
                  placeholder="Enter a strong password">
                  <template #suffix>
                    <NButton text @click="showPassword = !showPassword">
                      <NIcon>
                        <Eye v-if="!showPassword" />
                        <EyeOff v-else />
                      </NIcon>
                    </NButton>
                  </template>
                </NInput>
              </NFormItem>

              <NFormItem label="Confirm Password" required>
                <NInput v-model:value="passwordSetup.confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
                  placeholder="Confirm your password">
                  <template #suffix>
                    <NButton text @click="showConfirmPassword = !showConfirmPassword">
                      <NIcon>
                        <Eye v-if="!showConfirmPassword" />
                        <EyeOff v-else />
                      </NIcon>
                    </NButton>
                  </template>
                </NInput>
              </NFormItem>

              <div v-if="passwordSetup.password && passwordSetup.confirmPassword && !isPasswordValid"
                class="password-error">
                <NAlert type="error" title="Passwords don't match or are too short" />
              </div>
            </div>

            <NDivider />

            <!-- Security Questions -->
            <div class="form-section">
              <h3>Security Questions for Password Recovery</h3>
              <p class="section-description">
                These questions help you recover your password. Choose questions only you know the answer to.
              </p>

              <NFormItem label="Security Question 1" required>
                <NSelect v-model:value="passwordSetup.securityQuestion1" :options="securityQuestions"
                  placeholder="Choose a security question" />
              </NFormItem>

              <NFormItem label="Answer 1" required>
                <NInput v-model:value="passwordSetup.securityAnswer1" placeholder="Your answer (case sensitive)" />
              </NFormItem>

              <NFormItem label="Security Question 2" required>
                <NSelect v-model:value="passwordSetup.securityQuestion2"
                  :options="securityQuestions.filter(q => q.value !== passwordSetup.securityQuestion1)"
                  placeholder="Choose a different security question" />
              </NFormItem>

              <NFormItem label="Answer 2" required>
                <NInput v-model:value="passwordSetup.securityAnswer2" placeholder="Your answer (case sensitive)" />
              </NFormItem>

              <NFormItem label="Security Question 3" required>
                <NSelect v-model:value="passwordSetup.securityQuestion3" :options="securityQuestions.filter(q =>
                  q.value !== passwordSetup.securityQuestion1 &&
                  q.value !== passwordSetup.securityQuestion2
                )" placeholder="Choose a third security question" />
              </NFormItem>

              <NFormItem label="Answer 3" required>
                <NInput v-model:value="passwordSetup.securityAnswer3" placeholder="Your answer (case sensitive)" />
              </NFormItem>
            </div>
          </NForm>

          <template #footer>
            <NSpace justify="end">
              <NButton @click="cancelEdit">
                Cancel
              </NButton>
              <NButton type="primary" @click="goToStep2" :disabled="!canProceedToStep2">
                <template #icon>
                  <NIcon>
                    <ArrowRight />
                  </NIcon>
                </template>
                Continue
              </NButton>
            </NSpace>
          </template>
        </NCard>
      </div>

      <!-- Step 2: Essential Information (REQUIRED) -->
      <div v-if="currentStep === 2">
        <NCard title="Essential Information" class="step-card">
          <template #header-extra>
            <NIcon>
              <UserCheck />
            </NIcon>
          </template>

          <NAlert type="warning" title="Required Fields" class="required-notice">
            These fields are required to create your patient profile. You can add more details in the next steps.
          </NAlert>

          <NForm label-placement="top">
            <!-- Basic Personal Information -->
            <div class="form-section">
              <h3>Basic Information</h3>

              <NFormItem label="Date of Birth" required>
                <NDatePicker v-model:value="patientData.dateOfBirth" type="date" clearable value-format="yyyy-MM-dd"
                  :is-date-disabled="(timestamp) => timestamp > Date.now()" />
              </NFormItem>

              <NFormItem label="Gender" required>
                <NSelect v-model:value="patientData.gender" :options="genderOptions" placeholder="Select gender"
                  clearable />
              </NFormItem>

              <NFormItem label="Phone Number" required>
                <NInput v-model:value="patientData.phoneNumber" placeholder="Enter your phone number" />
              </NFormItem>

              <NFormItem label="Email Address">
                <NInput v-model:value="patientData.email" placeholder="Enter your email" />
              </NFormItem>

              <NFormItem label="Full Address">
                <NInput v-model:value="patientData.fullAddress" type="textarea" placeholder="Enter your full address"
                  :autosize="{ minRows: 2, maxRows: 4 }" />
              </NFormItem>
            </div>

            <NDivider />

            <!-- Emergency Contact -->
            <div class="form-section">
              <h3>Emergency Contact</h3>

              <NFormItem label="Emergency Contact Name" required>
                <NInput v-model:value="patientData.emergencyContactName" placeholder="Contact person's name" />
              </NFormItem>

              <NFormItem label="Emergency Contact Phone" required>
                <NInput v-model:value="patientData.emergencyContactPhone" placeholder="Emergency contact phone number" />
              </NFormItem>

              <NFormItem label="Relationship to Contact">
                <NInput v-model:value="patientData.emergencyContactRelationship"
                  placeholder="Spouse, Parent, Sibling, etc." />
              </NFormItem>
            </div>

            <NDivider />

            <!-- Consent -->
            <div class="form-section">
              <h3>Consent & Agreement</h3>

              <NFormItem label="Consent" required>
                <NCheckbox v-model:checked="patientData.consentSigned">
                  I consent to the storage and processing of my medical information
                </NCheckbox>
              </NFormItem>
            </div>
          </NForm>

          <!-- Error Messages -->
          <div v-if="errorMessage" class="message">
            <NAlert type="error" :title="errorMessage" closable />
          </div>

          <template #footer>
            <NSpace justify="space-between">
              <NButton @click="goToStep1" :disabled="isEditMode || isSaving" v-if="!isEditMode">
                <template #icon>
                  <NIcon>
                    <ArrowLeft />
                  </NIcon>
                </template>
                Back
              </NButton>

              <div v-else></div>

              <NSpace>
                <NButton @click="cancelEdit" :disabled="isSaving">
                  Cancel
                </NButton>
                <NButton type="primary" @click="goToStep3" :disabled="!isStep2Valid">
                  <template #icon>
                    <NIcon>
                      <ArrowRight />
                    </NIcon>
                  </template>
                  Continue
                </NButton>
              </NSpace>
            </NSpace>
          </template>
        </NCard>
      </div>

      <!-- Step 3: Medical Information (OPTIONAL) -->
      <div v-if="currentStep === 3">
        <NCard title="Medical Information" class="step-card">
          <template #header-extra>
            <NIcon>
              <Heart />
            </NIcon>
          </template>

          <NAlert type="info" title="Optional Medical Details" class="optional-notice">
            Add your medical history, allergies, medications, and other health information. All fields are optional.
          </NAlert>

          <NForm label-placement="top">
            <!-- Basic Medical Stats -->
            <div class="form-section">
              <h3>Basic Health Information</h3>

              <div class="form-row">
                <NFormItem label="Blood Group">
                  <NSelect v-model:value="patientData.bloodGroup" :options="bloodGroupOptions"
                    placeholder="Select blood group" clearable />
                </NFormItem>

                <NFormItem label="Height (cm)">
                  <NInputNumber v-model:value="patientData.heightCm" placeholder="Enter height in cm" clearable :min="1"
                    :max="300" />
                </NFormItem>
              </div>

              <NFormItem label="Weight (kg)">
                <NInputNumber v-model:value="patientData.weightKg" placeholder="Enter weight in kg" clearable :min="1"
                  :max="500" />
              </NFormItem>
            </div>

            <NDivider />

            <!-- Medical History -->
            <div class="form-section">
              <h3>Medical History & Conditions</h3>

              <NFormItem label="Medical History">
                <NDynamicInput v-model:value="patientData.medicalHistory" :on-create="createMedicalHistoryItem"
                  placeholder="Add medical condition">
                  <template #default="{ value, index }">
                    <NInput v-model:value="patientData.medicalHistory[index]" placeholder="Medical condition" />
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Allergies">
                <NDynamicInput v-model:value="patientData.allergies" :on-create="createAllergyItem"
                  placeholder="Add allergy">
                  <template #default="{ value, index }">
                    <NInput v-model:value="patientData.allergies[index]" placeholder="Allergy" />
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Current Medications">
                <NDynamicInput v-model:value="patientData.currentMedications" :on-create="createMedicationItem"
                  placeholder="Add medication">
                  <template #default="{ value, index }">
                    <NInput v-model:value="patientData.currentMedications[index]" placeholder="Medication" />
                  </template>
                </NDynamicInput>
              </NFormItem>
            </div>

            <NDivider />

            <!-- Additional Medical Records -->
            <div class="form-section">
              <h3>Additional Medical Records</h3>

              <NFormItem label="Immunizations">
                <NDynamicInput v-model:value="patientData.immunizations" :on-create="createImmunizationItem"
                  placeholder="Add immunization">
                  <template #default="{ value, index }">
                    <div class="dynamic-input-row">
                      <NInput v-model:value="patientData.immunizations[index].name" placeholder="Immunization name" />
                      <NDatePicker v-model:value="patientData.immunizations[index].date" type="date" clearable
                        value-format="yyyy-MM-dd" placeholder="Date received"
                        :is-date-disabled="(timestamp) => timestamp > Date.now()" />
                    </div>
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Family Medical History">
                <NDynamicInput v-model:value="patientData.familyMedicalHistory" :on-create="createFamilyHistoryItem"
                  placeholder="Add family history">
                  <template #default="{ value, index }">
                    <div class="dynamic-input-row">
                      <NInput v-model:value="patientData.familyMedicalHistory[index].condition"
                        placeholder="Medical condition" />
                      <NInput v-model:value="patientData.familyMedicalHistory[index].relation"
                        placeholder="Family relation" />
                    </div>
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Surgical History">
                <NDynamicInput v-model:value="patientData.surgicalHistory" :on-create="createSurgicalHistoryItem"
                  placeholder="Add surgical procedure">
                  <template #default="{ value, index }">
                    <div class="dynamic-input-row">
                      <NInput v-model:value="patientData.surgicalHistory[index].procedure"
                        placeholder="Surgical procedure" />
                      <NDatePicker v-model:value="patientData.surgicalHistory[index].date" type="date" clearable
                        value-format="yyyy-MM-dd" placeholder="Date of procedure"
                        :is-date-disabled="(timestamp) => timestamp > Date.now()" />
                    </div>
                  </template>
                </NDynamicInput>
              </NFormItem>
            </div>
          </NForm>

          <!-- Error Messages -->
          <div v-if="errorMessage" class="message">
            <NAlert type="error" :title="errorMessage" closable />
          </div>

          <template #footer>
            <NSpace justify="space-between">
              <NButton @click="goBackToStep2" :disabled="isSaving">
                <template #icon>
                  <NIcon>
                    <ArrowLeft />
                  </NIcon>
                </template>
                Back
              </NButton>

              <NSpace>
                <NButton @click="cancelEdit" :disabled="isSaving">
                  Cancel
                </NButton>
                <NButton type="primary" @click="goToStep4">
                  <template #icon>
                    <NIcon>
                      <ArrowRight />
                    </NIcon>
                  </template>
                  Continue
                </NButton>
              </NSpace>
            </NSpace>
          </template>
        </NCard>
      </div>

      <!-- Step 4: Lifestyle & Personal (OPTIONAL) -->
      <div v-if="currentStep === 4">
        <NCard title="Lifestyle & Personal Details" class="step-card">
          <template #header-extra>
            <NIcon>
              <Activity />
            </NIcon>
          </template>

          <NAlert type="info" title="Optional Information" class="optional-notice">
            Add your lifestyle habits and personal preferences. All fields are optional.
          </NAlert>

          <NForm label-placement="top">
            <!-- Personal Details -->
            <div class="form-section">
              <h3>Personal Details</h3>

              <div class="form-row">
                <NFormItem label="Occupation">
                  <NInput v-model:value="patientData.occupation" placeholder="Enter your occupation" />
                </NFormItem>

                <NFormItem label="Marital Status">
                  <NSelect v-model:value="patientData.maritalStatus" :options="maritalStatusOptions"
                    placeholder="Select marital status" clearable />
                </NFormItem>
              </div>

              <div class="form-row">
                <NFormItem label="Preferred Language">
                  <NSelect v-model:value="patientData.preferredLanguage" :options="languageOptions"
                    placeholder="Select your preferred language" clearable />
                </NFormItem>

                <NFormItem label="Communication Method">
                  <NSelect v-model:value="patientData.preferredCommunicationMethod"
                    :options="communicationMethodOptions" placeholder="Select preferred communication method"
                    clearable />
                </NFormItem>
              </div>
            </div>

            <NDivider />

            <!-- Lifestyle Information -->
            <div class="form-section">
              <h3>Lifestyle Habits</h3>

              <NGrid cols="2" x-gap="12" responsive="screen">
                <NGridItem>
                  <NFormItem label="Exercise">
                    <NSelect v-model:value="patientData.lifestyleInfo.exercise" placeholder="Exercise frequency"
                      :options="exerciseOptions" clearable />
                  </NFormItem>
                </NGridItem>
                <NGridItem>
                  <NFormItem label="Smoking">
                    <NSelect v-model:value="patientData.lifestyleInfo.smoking" placeholder="Smoking status"
                      :options="smokingOptions" clearable />
                  </NFormItem>
                </NGridItem>
                <NGridItem>
                  <NFormItem label="Alcohol">
                    <NSelect v-model:value="patientData.lifestyleInfo.alcohol" placeholder="Alcohol consumption"
                      :options="alcoholOptions" clearable />
                  </NFormItem>
                </NGridItem>
                <NGridItem>
                  <NFormItem label="Diet">
                    <NSelect v-model:value="patientData.lifestyleInfo.diet" placeholder="Diet type"
                      :options="dietOptions" clearable />
                  </NFormItem>
                </NGridItem>
                <NGridItem>
                  <NFormItem label="Sleep (hours)">
                    <NInputNumber v-model:value="patientData.lifestyleInfo.sleepHours" placeholder="Hours per night"
                      :min="1" :max="24" clearable />
                  </NFormItem>
                </NGridItem>
                <NGridItem>
                  <NFormItem label="Stress Level">
                    <NSelect v-model:value="patientData.lifestyleInfo.stressLevel" placeholder="Typical stress level"
                      :options="stressLevelOptions" clearable />
                  </NFormItem>
                </NGridItem>
              </NGrid>
            </div>
          </NForm>

          <!-- Error Messages -->
          <div v-if="errorMessage" class="message">
            <NAlert type="error" :title="errorMessage" closable />
          </div>

          <template #footer>
            <NSpace justify="space-between">
              <NButton @click="goBackToStep3" :disabled="isSaving">
                <template #icon>
                  <NIcon>
                    <ArrowLeft />
                  </NIcon>
                </template>
                Back
              </NButton>

              <NSpace>
                <NButton @click="cancelEdit" :disabled="isSaving">
                  Cancel
                </NButton>
                <NButton type="primary" @click="goToStep5">
                  <template #icon>
                    <NIcon>
                      <ArrowRight />
                    </NIcon>
                  </template>
                  Continue
                </NButton>
              </NSpace>
            </NSpace>
          </template>
        </NCard>
      </div>

      <!-- Step 5: Insurance & Healthcare (OPTIONAL) -->
      <div v-if="currentStep === 5">
        <NCard title="Insurance & Healthcare" class="step-card">
          <template #header-extra>
            <NIcon>
              <Building />
            </NIcon>
          </template>

          <NAlert type="info" title="Final Step" class="optional-notice">
            Add insurance and healthcare provider information. All fields are optional and can be updated later.
          </NAlert>

          <NForm label-placement="top">
            <!-- Insurance Information -->
            <div class="form-section">
              <h3>Insurance Information</h3>

              <div class="form-row">
                <NFormItem label="Insurance Provider">
                  <NInput v-model:value="patientData.insuranceProvider" placeholder="Blue Cross, Aetna, etc." />
                </NFormItem>

                <NFormItem label="Insurance ID">
                  <NInput v-model:value="patientData.insuranceId" placeholder="Your insurance ID" />
                </NFormItem>
              </div>

              <div class="form-row">
                <NFormItem label="Group Number">
                  <NInput v-model:value="patientData.insuranceGroupNumber" placeholder="Your insurance group number" />
                </NFormItem>

                <NFormItem label="Secondary Insurance">
                  <NInput v-model:value="patientData.secondaryInsuranceProvider"
                    placeholder="Secondary insurance (if any)" />
                </NFormItem>
              </div>
            </div>

            <NDivider />

            <!-- Healthcare & Pharmacy -->
            <div class="form-section">
              <h3>Healthcare Providers</h3>

              <div class="form-row">
                <NFormItem label="Primary Care Provider">
                  <NInput v-model:value="patientData.primaryCareProvider" placeholder="Primary care doctor's name" />
                </NFormItem>

                <NFormItem label="Last Physical Exam">
                  <NDatePicker v-model:value="patientData.lastPhysicalExam" type="date" clearable
                    value-format="yyyy-MM-dd" :is-date-disabled="(timestamp) => timestamp > Date.now()" />
                </NFormItem>
              </div>

              <div class="form-row">
                <NFormItem label="Preferred Pharmacy">
                  <NInput v-model:value="patientData.preferredPharmacy" placeholder="Preferred pharmacy name" />
                </NFormItem>

                <NFormItem label="Pharmacy Address">
                  <NInput v-model:value="patientData.preferredPharmacyAddress"
                    placeholder="Pharmacy address or location" />
                </NFormItem>
              </div>
            </div>

            <NDivider />

            <!-- Additional Healthcare Info -->
            <div class="form-section">
              <h3>Additional Healthcare Information</h3>

              <NFormItem label="Advance Directives">
                <NSwitch v-model:value="patientData.advanceDirectives">
                  <template #checked>Yes</template>
                  <template #unchecked>No</template>
                </NSwitch>
                <span class="directive-note">I have advance directives in place</span>
              </NFormItem>

              <NFormItem label="Accessibility Needs">
                <NSelect v-model:value="patientData.accessibilityNeeds" multiple :options="accessibilityNeedsOptions"
                  placeholder="Select any accessibility needs" clearable />
              </NFormItem>

              <NFormItem label="Dietary Restrictions">
                <NDynamicInput v-model:value="patientData.dietaryRestrictions" :on-create="createDietaryRestrictionItem"
                  placeholder="Add dietary restriction">
                  <template #default="{ value, index }">
                    <NInput v-model:value="patientData.dietaryRestrictions[index]" placeholder="Dietary restriction" />
                  </template>
                </NDynamicInput>
              </NFormItem>

              <NFormItem label="Additional Notes">
                <NInput v-model:value="patientData.notes" type="textarea"
                  placeholder="Additional notes about your health" :autosize="{ minRows: 2, maxRows: 6 }" />
              </NFormItem>
            </div>
          </NForm>

          <!-- Error/Success Messages -->
          <div v-if="errorMessage" class="message">
            <NAlert type="error" :title="errorMessage" closable />
          </div>

          <div v-if="successMessage" class="message">
            <NAlert type="success" :title="successMessage" />
          </div>

          <template #footer>
            <NSpace justify="space-between">
              <NButton @click="goBackToStep4" :disabled="isSaving">
                <template #icon>
                  <NIcon>
                    <ArrowLeft />
                  </NIcon>
                </template>
                Back
              </NButton>

              <NSpace>
                <NButton @click="cancelEdit" :disabled="isSaving">
                  Cancel
                </NButton>
                <NButton type="primary" @click="saveProfile" :loading="isSaving" :disabled="!isProfileDataValid">
                  <template #icon>
                    <NIcon>
                      <Save />
                    </NIcon>
                  </template>
                  {{ isEditMode ? 'Save Changes' : 'Create Profile' }}
                </NButton>
              </NSpace>
            </NSpace>
          </template>
        </NCard>
      </div>
    </div>
  </div>
</template>

<style scoped>
.profile-edit-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
}

.loading-state p {
  margin-top: 16px;
  color: #666;
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.page-header h1 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 2.2rem;
  font-weight: 600;
}

.page-subtitle {
  margin: 0;
  color: #666;
  font-size: 1.1rem;
}

.steps-container {
  margin-bottom: 32px;
}

.step-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.password-modal-content {
  text-align: center;
  padding: 16px 0;
}

.modal-icon {
  margin-bottom: 16px;
}

.security-notice,
.required-notice,
.optional-notice {
  margin-bottom: 24px;
}

.form-section {
  margin-bottom: 24px;
}

.form-section h3 {
  margin: 0 0 16px 0;
  color: #333;
  font-size: 1.3rem;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.section-description {
  margin: 0 0 20px 0;
  color: #666;
  font-size: 0.95rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.dynamic-input-row {
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
}

.dynamic-input-row>* {
  flex: 1;
}

.directive-note {
  margin-left: 10px;
  font-size: 0.9rem;
  color: #666;
}

.password-error,
.message {
  margin: 16px 0;
}

.error-message {
  margin-top: 16px;
}

/* Responsive design */
@media (max-width: 768px) {
  .profile-edit-container {
    padding: 10px;
  }

  .page-header h1 {
    font-size: 1.8rem;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .dynamic-input-row {
    flex-direction: column;
    gap: 8px;
  }

  .steps-container {
    margin-bottom: 20px;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.6rem;
  }

  .page-subtitle {
    font-size: 1rem;
  }
}
</style>